<template>
    <Head title="Home" />
    <BaseLayout gap="xl">
        <template #header>
            <!-- Schedule dialog kept for programmatic open (e.g. ?schedule_followup);
                 the top-right trigger buttons were removed. -->
            <ScheduleFollowupDialog ref="scheduleFollowupDialog" analytics-source="homepage" :coach-followup-events="coach_followup_events" />
        </template>
        <MobileAppBanner
            v-if="mobile_app_banner && !organizationDetails?.is_mobile_app_blocked"
            :download-url="mobile_app_banner.download_url"
            :store-name="mobile_app_banner.store_name" />
        <div class="flex flex-col gap-6">
            <div class="flex flex-col gap-4">
                <!-- Org disclaimer -->
                <HomeDisclaimerMessage theme="info" :message="disclaimer_message ?? undefined" />

                <!-- User greeting -->
                <div class="flex gap-4 mt-10">
                    <h1 class="display-serif max-w-125 text-balance">{{ pageHeaderGreeting }}</h1>
                </div>
            </div>
            <!-- Start chat input area -->
            <HomeChatInput
                :document-upload-enabled="document_upload_enabled"
                :visual-document-analysis-enabled="visual_document_analysis_enabled"
                :work-document-capabilities-url="work_document_capabilities_url"
                :default-chat-redirect-url="default_chat_redirect_url"
                :placeholder="chatBarPlaceholder"
                :pii-patterns="pii_patterns"
                :better-voice-composer-enabled="better_voice_composer_enabled" />
        </div>

        <!-- 360 Rater invite card (time-sensitive, shown before other sections) -->
        <RaterAssessmentHomeCard :invites="pending_rater_invites ?? []" />

        <!-- Wrapped invite card (seasonal) -->
        <HomeWrapped v-if="wrapped.can_start" :wrapped-url="wrapped.url" :wrapped-year="wrapped.year" :user-conversation-count="10" />

        <PerformanceManagementHomeCard
            :enabled="!!performance_review_moments?.moments?.length"
            :title="growthMomentsTitle"
            :homepage-card-title="performance_review_moments?.homepage_card_title ?? ''"
            :homepage-card-subtitle="performance_review_moments?.homepage_card_subtitle ?? ''"
            :moments="performance_review_moments?.moments ?? []" />

        <!-- The two WorkIQ sections, both only present for WorkIQ users with data. Insights lead
             because they are claims about now; the trajectory is the slower, reflective read and sits
             under it. -->
        <HomeMyWorkInsightsCard v-if="workiq_mywork_insights" :insights="workiq_mywork_insights" />

        <!-- WorkIQ longitudinal working patterns (PLT-1866); a second card over the same snapshot's
             metrics, not replaced by the insights one above -->
        <HomeMyWorkProgressCard v-if="mywork_progress" :progress="mywork_progress" />

        <HomeDiscoverBar
            v-if="setupPropsReady"
            :use-cases="discover_use_cases"
            :perspective-report-enabled="perspective_report_enabled"
            :perspective-report="perspective_report"
            :perspective-assessment-enabled="perspective_assessment_enabled"
            :perspective-card-dismissed="perspective_card_dismissed"
            :mywork-card="_props.mywork_card"
            :profile-completion-card="profile_completion_card"
            :guided-setup-card="_props.guided_setup_card"
            :guided-setup-home-promotion="guidedSetupHomePromotion"
            :setup-navigation-pending="setupNavigationPending"
            :setup-navigation-failed="setupNavigationFailed"
            :collaboration-profile-enabled="collaboration_profile_enabled"
            :has-collaboration-profile="_props.has_collaboration_profile"
            :homepage-mywork-insights-enabled="_props.homepage_mywork_insights_enabled"
            :homepage-mywork-insights="_props.homepage_mywork_insights"
            :homepage-mywork-feed="_props.homepage_mywork_feed"
            @insight-hover="handleUseCaseHover"
            @insight-hover-end="handleUseCaseHoverEnd"
            @insight-clicked="startChatBarUseCase"
            @setup-continue="continueGuidedSetup"
            @setup-retired="retireGuidedSetup" />
        <BaseNotification v-if="setupRefreshFailed" theme="error" hide-dismiss :aria-busy="setupRefreshPending">
            <template #content>
                <div class="flex flex-wrap items-center justify-between gap-4">
                    <p class="body-small">{{ t("coachingSetup.home.refreshError") }}</p>
                    <BaseButton theme="tertiary" size="sm" :disabled="setupRefreshPending" @click="refreshSetupProps">{{ t("common.tryAgain") }}</BaseButton>
                </div>
            </template>
        </BaseNotification>

        <!-- Tabs for remaining cards -->
        <BaseTabs v-model="activeTab" :tab-ids="visibleTabs" :labels="tabLabels" list-class="homepage-tabs-list">
            <!-- Calendar Tab -->
            <template #content-calendar>
                <HomeCalendarTab
                    id="homepage-calendar"
                    class="scroll-mt-10"
                    :initial-calendar-events="initial_calendar_events"
                    :calendar-coach-use-case="calendar_coach_use_case"
                    :calendar-insights-enabled="calendar_insights_homepage_enabled"
                    :calendar-locked-preview="calendar_locked_preview"
                    @coach-hover="handleUseCaseHover"
                    @coach-hover-end="handleUseCaseHoverEnd" />
            </template>

            <!-- Insights Tab -->
            <template #content-insights>
                <HomeInsightsTab :suggested-topics="suggested_topics" :insights="insights" @cta-click="handleCtaClick" />
            </template>

            <!-- Tracking Progress Tab -->
            <template #content-tracking-progress>
                <HomeTrackingProgressTab
                    :action-items="action_items"
                    :coach-follow-up-events="coach_followup_events"
                    :default-chat-redirect-url="default_chat_redirect_url"
                    :user-feedback="user_feedback"
                    :wrapped="wrapped"
                    @cta-click="handleCtaClick" />
            </template>

            <!-- Improving at [Org] Tab-->
            <template #content-org-specific>
                <HomeImprovingAtOrgTab :prompts="organization_prompts" :suggested-topics="organization_suggested_topics" @cta-click="handleCtaClick" />
            </template>
        </BaseTabs>
    </BaseLayout>

    <CookieBanner />
    <PreferredLanguageNotice />

    <!-- Redirect error message, if any-->
    <BaseToast v-if="toast_error" theme="error">
        <template #description>
            <MarkdownText class="body-small" :text="toast_error" />
        </template>
    </BaseToast>

    <!-- Calendar Feature Carousel Dialog -->
    <!-- The carousel owns its edge-to-edge slides, navigation and final connection action. -->
    <BaseDialog
        v-if="setupCarouselsAllowed && shouldShowCalendarCarousel"
        v-model:open="isCalendarCarouselOpen"
        layout="custom"
        title="Connect your calendar"
        dialog-content-class="z-60 max-h-[85dvh] md:max-h-[90dvh]"
        overlay-class="z-60"
        @update:open="handleCalendarDialogOpenChange">
        <div class="flex justify-between items-center px-4 md:px-10 pt-6">
            <h3 class="flex items-center gap-2 title-body"><i class="bi bi-calendar" aria-hidden="true"></i> Connect your Calendar</h3>
            <button type="button" class="bi bi-x-lg icon-semibold" aria-label="Close" title="close feature carousel" @click="handleCalendarDialogOpenChange(false)"></button>
        </div>
        <FeatureCarousel @connect="handleConnectCalendar" />
    </BaseDialog>

    <!-- Calendar connection dialog. Lives at page level rather than inside the calendar tab so the
         store flag can be set from anywhere on the page (feature carousel, ?connect_calendar) — the
         tab's subtree is unmounted whenever another tab is active. -->
    <CalendarIntegrationSettings
        v-if="calendar_integration.enabled"
        v-model:open="useCalendarStore.showConnectCalendarDialog"
        :source="useCalendarStore.connectCalendarSource ?? 'home'"
        :deeplink-source="useCalendarStore.connectCalendarDeeplinkSource"
        @close="useCalendarStore.setShowConnectCalendarDialog(false)" />

    <!-- Coaching Context Carousel Dialog -->
    <!-- The coaching-context carousel supplies its own full-bleed slide chrome. -->
    <BaseDialog
        v-if="setupCarouselsAllowed && isCoachingContextCarouselOpen"
        v-model:open="isCoachingContextCarouselOpen"
        layout="custom"
        title="Get more from Nadia"
        dialog-content-class="z-60 md:max-w-4xl max-h-[85dvh] md:max-h-[90dvh] bg-surface"
        overlay-class="z-60"
        @update:open="handleCoachingContextCarouselOpenChange">
        <CoachingContextCarousel
            :first-name="userDetails?.first_name ?? undefined"
            location="home"
            :connections-enabled="_props.coaching_context_carousel?.connections_enabled ?? true"
            :collab-profile-enabled="_props.coaching_context_carousel?.collab_profile_enabled ?? true"
            :calendar-enabled="_props.coaching_context_carousel?.calendar_enabled ?? true"
            :cta-loading="isCoachingContextCarouselCtaLoading"
            @dismiss="handleCoachingContextCarouselOpenChange(false)"
            @cta-click="handleCoachingContextCarouselCtaClick" />
    </BaseDialog>
</template>

<script lang="ts">
import { convertToDateTime } from "/js/dateUtils";
import LeftSidebarLayout from "~vue/layouts/LeftSidebarLayout.vue";
export default { layout: LeftSidebarLayout };
</script>

<script setup lang="ts">
import { Head, router, usePage, useRemember } from "@inertiajs/vue3";
import { useFetch } from "~vue/api";
import type { GlobalProperties, OrganizationDetails, PerspectiveReport, UserDetails } from "~vue/appTypes";
import CoachingContextCarousel from "~vue/ChatHome/CoachingContextCarousel.vue";
import HomeCalendarTab from "~vue/ChatHome/HomeCalendarTab.vue";
import HomeChatInput from "~vue/ChatHome/HomeChatInput.vue";
import HomeDisclaimerMessage from "~vue/ChatHome/HomeDisclaimerMessage.vue";
import HomeDiscoverBar from "~vue/ChatHome/HomeDiscoverBar.vue";
import HomeImprovingAtOrgTab from "~vue/ChatHome/HomeImprovingAtOrgTab.vue";
import HomeInsightsTab from "~vue/ChatHome/HomeInsightsTab.vue";
import HomeMyWorkInsightsCard from "~vue/ChatHome/HomeMyWorkInsightsCard.vue";
import HomeMyWorkProgressCard from "~vue/ChatHome/HomeMyWorkProgressCard.vue";
import HomeTrackingProgressTab from "~vue/ChatHome/HomeTrackingProgressTab.vue";
import HomeWrapped from "~vue/ChatHome/HomeWrapped.vue";
import MobileAppBanner from "~vue/ChatHome/MobileAppBanner.vue";
import PerformanceManagementHomeCard from "~vue/ChatHome/PerformanceManagementHomeCard.vue";
import RaterAssessmentHomeCard from "~vue/ChatHome/RaterAssessmentHomeCard.vue";
import { isRolePlayUseCase } from "~vue/ChatUseCaseEntrance/utils";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseDialog from "~vue/components/BaseDialog.vue";
import BaseLayout from "~vue/components/BaseLayout.vue";
import BaseNotification from "~vue/components/BaseNotification.vue";
import BaseTabs from "~vue/components/BaseTabs.vue";
import BaseToast from "~vue/components/BaseToast.vue";
import CalendarIntegrationSettings from "~vue/components/CalendarIntegration/CalendarIntegrationSettings.vue";
import FeatureCarousel from "~vue/components/CalendarIntegration/FeatureCarousel.vue";
import CookieBanner from "~vue/components/CookieBanner.vue";
import PreferredLanguageNotice from "~vue/components/PreferredLanguageNotice.vue";
import ScheduleFollowupDialog from "~vue/components/ScheduleFollowupDialog.vue";
import { useUIInteractions } from "~vue/composables/useUIInteractions";
import MarkdownText from "~vue/MarkdownText.vue";
import type { GuidedSetupHomePromotion, SetupHomeCard } from "~vue/ProfileSetup/types";
import { returnToCalendarAfterConnect, useCalendarStore } from "~vue/stores/calendarStore";
import type { CalendarIntegrationViewEvent } from "~vue/types/calendar_integration";
import type { ChatEntranceUseCaseData } from "~vue/types/chat";
import type {
    ActionItem,
    CalendarIntegration,
    ChatEntranceUseCasesResponse,
    CoachFollowupEvent,
    HomeCard,
    HomepageCustomization,
    HomepageInsight,
    HomepageMyWorkInsight,
    MyWorkCardData,
    MyWorkInsightsCardData,
    MyWorkProgressData,
    OrganizationPrompt,
    OrganizationSuggestedTopic,
    PendingRaterInvite,
    ProfileCompletionSummary,
    SuggestedTopic,
    UserFeedback,
    Wrapped,
} from "~vue/types/chat_home";
import type { PerformanceReviewMoments } from "~vue/types/performance_management";
import { isInIFrame, openUrl } from "~vue/utils";
import AmplitudeEventNames from "~vue/utils/amplitudeEventNames";
import { CHATSOURCE, HOMEPAGE_CARD_LOCATION } from "~vue/utils/constants.js";
import { CARD_TYPE } from "~vue/utils/homeCards";
import { logUserInteraction } from "~vue/utils/logUtils";
import type { PatternEntry } from "~vue/utils/pii/keywordClassifier";
import { DateTime } from "luxon";
import { computed, inject, onMounted, onUnmounted, provide, type Ref, ref, toValue, useTemplateRef, watch } from "vue";
import { useI18n } from "vue-i18n";

export interface ChatHomeProps {
    coach_followup_events: CoachFollowupEvent[];
    default_chat_redirect_url: string;
    event_context: { window_id: string };
    disclaimer_message: string | null;
    is_first_time_user: boolean;
    action_items: ActionItem[];
    suggested_topics: SuggestedTopic[];
    organization_prompts: OrganizationPrompt[];
    organization_suggested_topics: OrganizationSuggestedTopic[];
    insights?: HomepageInsight[];
    role_play_setup_url: string;
    user_feedback: UserFeedback;
    calendar_insights_homepage_enabled: boolean;
    calendar_locked_preview?: boolean;
    document_upload_enabled: boolean;
    visual_document_analysis_enabled: boolean;
    work_document_capabilities_url: string;
    calendar_integration: CalendarIntegration;
    initial_calendar_events: CalendarIntegrationViewEvent[] | null;
    calendar_coach_use_case: ChatEntranceUseCaseData | null;
    toast_error: string | null;
    is_feature_carousel_dismissed: boolean;
    coaching_context_carousel?: {
        should_show: boolean;
        connections_enabled: boolean;
        collab_profile_enabled: boolean;
        calendar_enabled: boolean;
        cta_url: string;
    };
    discover_use_cases: ChatEntranceUseCasesResponse;
    wrapped: Wrapped;
    perspective_report_enabled: boolean;
    perspective_report: PerspectiveReport | null;
    perspective_assessment_enabled: boolean;
    perspective_card_dismissed: boolean;
    collaboration_profile_enabled: boolean;
    has_collaboration_profile: boolean;
    performance_review_moments: PerformanceReviewMoments;
    pii_patterns?: PatternEntry[] | null;
    homepage_insights_tab_enabled: boolean;
    homepage_customization?: HomepageCustomization | null;
    better_voice_composer_enabled?: boolean;
    pending_rater_invites?: PendingRaterInvite[];
    mywork_card?: MyWorkCardData | null;
    mywork_progress?: MyWorkProgressData | null;
    workiq_mywork_insights?: MyWorkInsightsCardData | null;
    profile_completion_card?: ProfileCompletionSummary | null;
    guided_setup_card?: SetupHomeCard | null;
    guided_setup_home_promotion?: GuidedSetupHomePromotion | null;
    hide_nadia_name?: boolean;
    homepage_mywork_insights_enabled?: boolean;
    homepage_mywork_insights?: HomepageMyWorkInsight[];
    homepage_mywork_feed?: import("~vue/types/labs").MyWorkInsightFeed | null;
    workiq_ff_enabled?: boolean;
    has_workiq_connected?: boolean;
    mobile_app_banner?: {
        download_url: string;
        store_name: string;
    } | null;
}

const _props = withDefaults(defineProps<ChatHomeProps>(), {
    action_items: () => [],
    calendar_coach_use_case: null,
    calendar_insights_homepage_enabled: false,
    calendar_locked_preview: false,
    calendar_integration: () => ({ enabled: false, timezones: [], calendar_provider: null, dismissed: false, private_toggle_enabled: false, allow_private_events: null }),
    coach_followup_events: () => [],
    default_chat_redirect_url: "/",
    disclaimer_message: null,
    discover_use_cases: () => ({ enabled: false, cases: [] }),
    document_upload_enabled: true,
    visual_document_analysis_enabled: false,
    work_document_capabilities_url: "",
    event_context: () => ({ window_id: "chat_home" }),
    initial_calendar_events: null,
    insights: () => [],
    is_feature_carousel_dismissed: false,
    coaching_context_carousel: undefined,
    is_first_time_user: true,
    organization_prompts: () => [],
    organization_suggested_topics: () => [],
    performance_review_moments: () => ({}),
    toast_error: null,
    user_feedback: () => ({ enabled: false, feedback_response_count_min: null }),
    perspective_report_enabled: false,
    perspective_report: null,
    perspective_assessment_enabled: false,
    perspective_card_dismissed: false,
    collaboration_profile_enabled: false,
    has_collaboration_profile: false,
    wrapped: () => ({ can_access: false, can_start: false, show_completed: false, url: "", year: 0, action_items: [] }),
    pii_patterns: null,
    homepage_insights_tab_enabled: false,
    better_voice_composer_enabled: false,
    pending_rater_invites: () => [],
    hide_nadia_name: false,
    suggested_topics: () => [],
    homepage_customization: null,
    mywork_card: null,
    mywork_progress: null,
    workiq_mywork_insights: null,
    profile_completion_card: null,
    guided_setup_card: null,
    guided_setup_home_promotion: null,
    homepage_mywork_insights_enabled: false,
    homepage_mywork_insights: () => [],
    homepage_mywork_feed: null,
    workiq_ff_enabled: false,
    has_workiq_connected: false,
    mobile_app_banner: null,
});

const { t } = useI18n();
const homeHistory = useRemember<{ visited?: boolean; interactionCount: number | null }>({ visited: false, interactionCount: null }, "ChatHome:setupPromotion");
// Keep the clicked offer mounted for navigation errors; history restores it as consumed.
const retiredInteractionCount = ref(toValue(homeHistory).interactionCount);
// Older history entries have no visit marker and also need revalidation.
const isHistoryReturn = toValue(homeHistory).visited !== false;
const setupPropsReady = ref(!(isHistoryReturn && (_props.guided_setup_card || _props.guided_setup_home_promotion)));
const setupRefreshPending = ref(false);
const setupRefreshFailed = ref(false);
// Initial navigation can overwrite remembered state during mount; save it before the user leaves instead.
const stopRememberHomeVisit = router.on("before", () => {
    toValue(homeHistory).visited = true;
});
const guidedSetupHomePromotion = computed(() => {
    const promotion = _props.guided_setup_home_promotion;
    return promotion && promotion.interaction_count === retiredInteractionCount.value ? { ...promotion, is_due: false } : promotion;
});
const setupCarouselsAllowed = computed(() => setupPropsReady.value && guidedSetupHomePromotion.value === null);

const setupNavigationPending = ref(false);
const setupNavigationFailed = ref(false);

function refreshSetupProps(): void {
    if (setupRefreshPending.value) {
        return;
    }
    setupRefreshPending.value = true;
    router.reload({
        only: [
            "user_details",
            "guided_setup_card",
            "guided_setup_home_promotion",
            "coaching_context_carousel",
            "mywork_card",
            "homepage_mywork_insights_enabled",
            "homepage_mywork_insights",
            "homepage_mywork_feed",
            "perspective_assessment_completed",
            "perspective_archetype",
            "has_collaboration_profile",
        ],
        onSuccess: () => {
            setupPropsReady.value = true;
            setupRefreshFailed.value = false;
            isCoachingContextCarouselOpen.value = _props.coaching_context_carousel?.should_show ?? false;
        },
        onFinish: () => {
            setupRefreshPending.value = false;
            setupRefreshFailed.value = !setupPropsReady.value;
        },
    });
}

function retireGuidedSetup(interactionCount: number): void {
    retiredInteractionCount.value = interactionCount;
    toValue(homeHistory).interactionCount = interactionCount;
}

function continueGuidedSetup(url: string, interactionCount: number): void {
    toValue(homeHistory).interactionCount = interactionCount;
    if (isInIFrame()) {
        openUrl(url);
        return;
    }

    setupNavigationFailed.value = false;
    let succeeded = false;
    router.visit(url, {
        onStart: () => {
            setupNavigationPending.value = true;
        },
        onSuccess: () => {
            succeeded = true;
        },
        // Inertia also finishes network exceptions and cancellations, which bypass onError.
        onFinish: () => {
            setupNavigationPending.value = false;
            setupNavigationFailed.value = !succeeded;
        },
    });
}

const {
    action_items,
    calendar_coach_use_case,
    calendar_insights_homepage_enabled,
    calendar_locked_preview,
    calendar_integration,
    coach_followup_events,
    default_chat_redirect_url,
    disclaimer_message,
    discover_use_cases,
    document_upload_enabled,
    visual_document_analysis_enabled,
    work_document_capabilities_url,
    event_context,
    initial_calendar_events,
    is_feature_carousel_dismissed,
    is_first_time_user,
    organization_prompts,
    organization_suggested_topics,
    performance_review_moments,
    toast_error,
    user_feedback,
    perspective_report_enabled,
    perspective_report,
    perspective_assessment_enabled,
    collaboration_profile_enabled,
    perspective_card_dismissed,
    wrapped,
    pii_patterns,
    homepage_insights_tab_enabled,
    better_voice_composer_enabled,
    pending_rater_invites,
    hide_nadia_name,
    suggested_topics,
    insights,
    mywork_progress,
    workiq_mywork_insights,
    profile_completion_card,
    mobile_app_banner,
} = _props;

provide("calendarIntegration", calendar_integration);

const globalProperties = inject<GlobalProperties>("globalProperties");
const userDetails = inject<Ref<UserDetails>>("user_details");
const organizationDetails = inject<Ref<OrganizationDetails>>("organization_details");

const scheduleFollowupDialog = useTemplateRef("scheduleFollowupDialog");

const pageHeaderGreeting = computed(() => {
    const userFirstName = userDetails?.value?.first_name;
    if (is_first_time_user) {
        if (hide_nadia_name) {
            return `Hi ${userFirstName ?? "there"}, welcome to your AI Coach`;
        }
        return `Hi ${userFirstName ?? "there"}, I'm Nadia, your AI Coach`;
    }
    return `Welcome back${userFirstName ? `, ${userFirstName}` : ""}`;
});

const perfReviewCustomCopy = computed(() => organizationDetails?.value?.perf_review_custom_copy ?? {});
const growthMomentsTitle = computed(() => perfReviewCustomCopy.value?.page_tab_label || "Performance Management");
const homepageCustomization = computed(() => _props.homepage_customization);
const orgTabLabel = computed(() => {
    const prefix = homepageCustomization.value?.org_tab_prefix?.trim() ?? "Succeeding at";
    const orgName = organizationDetails?.value?.name?.trim() ?? "Your Organization";
    return `${prefix} ${orgName}`;
});
const progressTabLabel = computed(() => homepageCustomization.value?.progress_tab_name?.trim() || "Tracking Progress");
const calendarTabLabel = computed(() => homepageCustomization.value?.calendar_tab_name?.trim() || "Calendar");
const tabLabels = computed(() => ({
    calendar: calendarTabLabel.value,
    insights: "Insights",
    "tracking-progress": progressTabLabel.value,
    "org-specific": orgTabLabel.value,
}));

const startChatBarUseCase = (use_case: ChatEntranceUseCaseData) => {
    if (isRolePlayUseCase(use_case)) {
        openUrl(_props.role_play_setup_url);
        return;
    }
    openUrl(default_chat_redirect_url, {
        queryParams: {
            chat_source: use_case.chat_source,
            script: use_case.call_code,
        },
    });
};

const handleUseCaseHover = (useCase: ChatEntranceUseCaseData) => {
    hoveredButtonDescription.value = useCase.description ?? undefined;
};

const handleUseCaseHoverEnd = () => {
    hoveredButtonDescription.value = undefined;
};

enum TabId {
    Calendar = "calendar",
    TrackingProgress = "tracking-progress",
    OrgSpecific = "org-specific",
    Insights = "insights",
}

const isOrgSpecificTabVisible = computed(() => !!(organization_prompts.length || organization_suggested_topics.length));
const isInsightsTabVisible = computed(() => homepage_insights_tab_enabled && !!(suggested_topics.length || insights.length));
const isCalendarTabVisible = computed(() => !!calendar_integration.enabled);
// This logic should match the visibleCardTypes computed in HomeTrackingProgressTab
const isTrackingProgressTabVisible = computed(
    () =>
        !!(
            action_items.length ||
            wrapped.show_completed ||
            wrapped.action_items.length ||
            user_feedback.enabled ||
            coach_followup_events.some((event) => DateTime.now().hasSame(convertToDateTime(event.event_at_confirmed), "day"))
        ),
);

const TAB_ORDER_KEY: Partial<Record<TabId, keyof NonNullable<NonNullable<typeof homepageCustomization.value>["tabs_order"]>>> = {
    [TabId.Calendar]: "calendar",
    [TabId.TrackingProgress]: "tracking_progress",
    [TabId.OrgSpecific]: "org_specific",
};

const visibleTabs = computed((): TabId[] => {
    const visible = [
        isCalendarTabVisible.value && TabId.Calendar,
        isOrgSpecificTabVisible.value && TabId.OrgSpecific,
        isInsightsTabVisible.value && TabId.Insights,
        isTrackingProgressTabVisible.value && TabId.TrackingProgress,
    ].filter((t): t is TabId => !!t);

    const tabsOrder = homepageCustomization.value?.tabs_order;
    if (!tabsOrder) {
        return visible;
    }

    const orderOf = (t: TabId) => {
        const key = TAB_ORDER_KEY[t];
        return (key && tabsOrder[key]) ?? Infinity;
    };
    return visible.toSorted((a, b) => orderOf(a) - orderOf(b));
});
// The feedback card only auto-opens its dialog from `?open_feedback=true` while mounted, so land on its tab.
const openFeedbackRequested = new URLSearchParams(globalThis.location.search).get("open_feedback") === "true";
const activeTab = ref<TabId>(openFeedbackRequested && visibleTabs.value.includes(TabId.TrackingProgress) ? TabId.TrackingProgress : visibleTabs.value[0]);
if (returnToCalendarAfterConnect.value && userDetails?.value?.has_calendar_integration && isCalendarTabVisible.value) {
    activeTab.value = TabId.Calendar;
}

watch(activeTab, (tab) => {
    if (tab) {
        logUserInteraction(AmplitudeEventNames.HOMEPAGE_TAB_CLICKED, { tab });
    }
});

const handleCtaClick = (card: HomeCard) => {
    logUserInteraction(AmplitudeEventNames.HOMEPAGE_CARD_CLICKED, {
        card_type: card.type,
        title: card.title,
        external_name: card.external_name,
        source: card.source,
        location: HOMEPAGE_CARD_LOCATION.HOME,
    });

    const cardUrl = card.url;
    if (!cardUrl) {
        return;
    }

    switch (card.type) {
        case CARD_TYPE.FOLLOW_UP:
            openUrl(cardUrl, { queryParams: { chat_source: CHATSOURCE.HOME_FOLLOW_UP } });
            break;
        case CARD_TYPE.ORGANIZATION:
            openUrl(cardUrl, { queryParams: { chat_source: CHATSOURCE.TRENDING_ORG_PROMPT } });
            break;
        case CARD_TYPE.SUGGESTED_TOPICS:
            openUrl(cardUrl, { queryParams: { chat_source: CHATSOURCE.HOME_SUGGESTED_TOPICS } });
            break;
        case CARD_TYPE.INSIGHTS:
            openUrl(cardUrl, { queryParams: { chat_source: CHATSOURCE.HOME_INSIGHTS } });
            break;
        default:
    }
};

const hoveredButtonDescription = ref<string | undefined>(undefined);
const chatBarPlaceholder = computed(() => hoveredButtonDescription.value ?? "What's on your mind?");

// Coaching context carousel — takes priority over other home screen popups
const isCoachingContextCarouselOpen = ref(false);
const isCoachingContextCarouselCtaLoading = ref(false);
const { createInteraction } = useUIInteractions();

const calendarFeatureCarouselEnabled = computed(() => {
    return calendar_integration.enabled && !calendar_integration.dismissed && !is_feature_carousel_dismissed && !userDetails?.value?.has_calendar_integration; // dont show if already integrated calendar
});

const isMobileApp = usePage().props?.is_mobile_app;
// Suppress calendar carousel when coaching context carousel should fire (it takes priority)
const shouldShowCalendarCarousel = computed(
    () => calendarFeatureCarouselEnabled.value && !isMobileApp && !_props.coaching_context_carousel?.should_show && !isCoachingContextCarouselOpen.value,
);

const isCalendarCarouselOpen = ref(shouldShowCalendarCarousel.value);

// Deep link that lands on the homepage with the calendar connection dialog already open, so a
// campaign email or Slack nudge can drop the user straight into the flow (ENG-1903).
const CONNECT_CALENDAR_PARAM = "connect_calendar";
// Optional attribution on that link, naming which campaign or nudge sent the user.
const DEEPLINK_SOURCE_PARAM = "source";
// The link is public, so its ?source is whatever the caller typed. Cap it before it becomes an
// Amplitude property value, since Amplitude keeps every distinct value it is ever sent.
const DEEPLINK_SOURCE_MAX_LENGTH = 64;

const openConnectCalendarDialog = (source: string, deeplinkSource?: string) => {
    isCalendarCarouselOpen.value = false;
    useCalendarStore.openConnectCalendarDialog(source, deeplinkSource);
};

const handleConnectCalendar = () => openConnectCalendarDialog("carousel");

const updateDismissPayload = ref<{ dismissed_feature_carousel_at: string } | null>(null);
const { execute: updateDismissFeatureCarousel } = useFetch("api/accounts/user", { immediate: false }).put(updateDismissPayload).json();

const handleCalendarDialogOpenChange = async (isOpen: boolean) => {
    if (!isOpen) {
        isCalendarCarouselOpen.value = false;
        if (!is_feature_carousel_dismissed) {
            updateDismissPayload.value = { dismissed_feature_carousel_at: DateTime.now().toISO() };
            await updateDismissFeatureCarousel();
            updateDismissPayload.value = null;
        }
    }
};

const handleCoachingContextCarouselOpenChange = async (isOpen: boolean) => {
    isCoachingContextCarouselOpen.value = isOpen;
    if (!isOpen) {
        logUserInteraction(AmplitudeEventNames.COACHING_CONTEXT_CAROUSEL_DISMISSED, { location: "home" });
        await createInteraction("coaching_context_carousel_dismissed");
    }
};

async function handleCoachingContextCarouselCtaClick() {
    isCoachingContextCarouselCtaLoading.value = true;
    try {
        await createInteraction("coaching_context_carousel_cta_clicked");
    } finally {
        isCoachingContextCarouselCtaLoading.value = false;
        isCoachingContextCarouselOpen.value = false;
    }
    openUrl(_props.coaching_context_carousel?.cta_url ?? "/accounts/user-profile");
}

onMounted(() => {
    globalProperties?.$setEventContext(event_context);
    if (!setupPropsReady.value) {
        // Browser Back can restore stale progress even when setup was entered through the sidebar.
        refreshSetupProps();
    }
    const params = new URLSearchParams(globalThis.location.search);
    if (params.get("schedule_followup")) {
        scheduleFollowupDialog.value?.open();
    }
    if (params.get(CONNECT_CALENDAR_PARAM) && calendar_integration.enabled) {
        openConnectCalendarDialog("deeplink", params.get(DEEPLINK_SOURCE_PARAM)?.trim().slice(0, DEEPLINK_SOURCE_MAX_LENGTH) || undefined);
    }
    if (_props.coaching_context_carousel?.should_show) {
        isCalendarCarouselOpen.value = false;
        isCoachingContextCarouselOpen.value = true;
    }
});

onUnmounted(() => {
    stopRememberHomeVisit();
    globalProperties?.$setEventContext({});
});
</script>

<style scoped>
@reference "../../../css/tailwind.css";

:deep(.homepage-tabs-list) {
    @apply lg:sticky lg:top-0 pt-4 mb-4 z-10 -mx-6 md:mx-0 px-6 md:px-0 bg-surface;
}
</style>
